import { timeDayHref } from '../pages/time/dayLink'
import { leaveHref } from '../pages/time/leaveLink'

export interface NotificationReason {
  rule: string
  label: string
  selector: string
}

export interface NoticeLike {
  kind: string
  params: Record<string, string>
  link_entity: string | null
  link_id: string | null
  reason?: NotificationReason | null
}

type Translate = (key: string, options?: Record<string, unknown>) => string
type Exists = (key: string) => boolean

/** `subject:<role>` means something different per kind; a pair missing here gets no why-line rather than a guess. */
export const SUBJECT_WHY: Readonly<Record<string, string>> = {
  contract_end_soon_employee: 'aboutYou',
  contract_end_past_employee: 'aboutYou',
  resignation_today_employee: 'aboutYou',
  hr_welcome_employee: 'aboutYou',
  hr_anniversary_employee: 'aboutYou',
  hr_birthday_employee: 'aboutYou',
  supervisor_changed_employee: 'aboutYou',
  supervisor_changed_supervisor: 'newSupervisor',
  request_decided_employee: 'yourRequest',
  equipment_request_decided_employee: 'yourRequest',
  timesheet_needs_approval_employee: 'youApprove',
  timesheet_rejected_employee: 'aboutYou',
  timesheet_past_edit_recipient: 'pastEdit',
  salary_cutoff_pending_employee: 'aboutYou',
  salary_cutoff_last_day_employee: 'aboutYou',
  timesheet_approval_deadline_employee: 'youApprove',
  timesheet_weekly_reminder_employee: 'logsHours',
  overwork_14_days_employee: 'overwork',
  annual_leave_unused_employee: 'aboutYou',
  leave_approval_pending_employee: 'youApprove',
  leave_submitted_employee: 'youApprove',
  leave_decided_employee: 'yourRequest',
  leave_cancel_refused_employee: 'yourRequest',
  leave_cancelled_employee: 'youApprove',
  leave_auto_rejected_employee: 'yourRequest',
  leave_settlement_decided_employee: 'yourRequest',
  leave_override_granted_employee: 'aboutYou',
  leave_peer_blocked_employee: 'yourRequest',
  leave_peer_clash_employee: 'overlap',
  leave_rule_overridden_employee: 'applicantOrOverlap',
  leave_break_starting_employee: 'aboutYou',
  roster_departure_soon_employee: 'aboutYou',
  roster_plan_changed_employee: 'aboutYou',
  oreas_request_received_recipient: 'oreasHolder',
  oreas_request_resolved_recipient: 'oreasHolder',
}

function isReason(value: unknown): value is NotificationReason {
  if (!value || typeof value !== 'object') return false
  const r = value as Record<string, unknown>
  return typeof r.rule === 'string' && typeof r.label === 'string' && typeof r.selector === 'string'
}

/** Why this notice reached its reader, or null when the row predates reasons or its selector has no wording. */
export function whyText(n: NoticeLike, t: Translate, exists: Exists): string | null {
  if (!isReason(n.reason)) return null
  const selector = n.reason.selector.trim()
  if (selector === 'actor') return t('notifications.why.actor')
  const colon = selector.indexOf(':')
  if (colon <= 0) return null
  const kind = selector.slice(0, colon)
  const value = selector.slice(colon + 1).trim()
  if (!value) return null
  if (kind === 'ability') {
    const labelKey = `admin.abilityName.${value.replaceAll('.', '_')}`
    return exists(labelKey) ? t('notifications.why.ability', { label: t(labelKey) }) : null
  }
  if (kind === 'level') return t('notifications.why.level', { level: value.toUpperCase() })
  if (kind === 'subject') {
    const phrase = SUBJECT_WHY[`${n.kind}_${value}`]
    return phrase ? t(`notifications.why.subject.${phrase}`) : null
  }
  return null
}

/** The rule's own name; absent on rows written before reasons were stored. */
export function ruleLabel(n: NoticeLike): string | null {
  return isReason(n.reason) && n.reason.label.trim() ? n.reason.label.trim() : null
}

/** Params worth showing, in reading order: [param, label, label is a shared `field.*` key]; anything unlisted is plumbing. */
export const DETAIL_FIELDS: ReadonlyArray<readonly [string, string, boolean]> = [
  ['title', 'title', true],
  ['document_no', 'no', true],
  ['organization', 'organization', true],
  ['name', 'name', true],
  ['employee_name', 'name', true],
  ['type_of', 'type', true],
  ['leave_type', 'leaveType', false],
  ['peer_leave_type', 'peerLeaveType', false],
  ['purpose', 'purpose', true],
  ['request', 'request', false],
  ['prepared_by', 'preparedBy', true],
  ['total', 'total', true],
  ['approved_amount', 'approvedAmount', true],
  ['currency', 'currency', true],
  ['status_before', 'statusBefore', false],
  ['approval_status', 'status', true],
  ['status', 'status', true],
  ['editor', 'decidedBy', false],
  ['decided_by', 'decidedBy', false],
  ['rejected_by', 'rejectedBy', false],
  ['actor', 'changedBy', false],
  ['granted_by', 'grantedBy', false],
  ['rule', 'rule', false],
  ['trigger_type', 'settlementFor', false],
  ['reason', 'reason', false],
  ['comment', 'comment', true],
  ['date', 'date', true],
  ['day', 'date', true],
  ['start_date', 'startDate', true],
  ['end_date', 'endDate', true],
  ['days', 'days', false],
  ['due_date', 'dueDate', true],
  ['cutoff_date', 'cutoffDate', false],
  ['pay_date', 'payDate', false],
  ['period', 'period', true],
  ['hours', 'hours', false],
  ['total_hours', 'hours', false],
  ['before', 'before', false],
  ['after', 'after', false],
  ['recipients', 'recipients', false],
  ['position', 'position', true],
  ['end_of_contract', 'endOfContract', false],
  ['days_remaining', 'daysRemaining', false],
  ['days_since_end', 'daysSinceEnd', false],
  ['resignation_date', 'resignationDate', false],
  ['previous_supervisor', 'previousSupervisor', false],
  ['new_supervisor', 'newSupervisor', false],
  ['project', 'project', false],
  ['role', 'role', true],
]

export interface DetailField {
  name: string
  label: string
  shared: boolean
  value: string
}

/** One row per non-blank param, dropping a repeat of the same label and value (`name` beside `employee_name`). */
export function detailFields(n: NoticeLike): DetailField[] {
  const params = n.params ?? {}
  const seen = new Set<string>()
  const out: DetailField[] = []
  for (const [name, label, shared] of DETAIL_FIELDS) {
    const raw = params[name]
    const value = typeof raw === 'string' ? raw.trim() : ''
    const id = `${shared}:${label}\u0000${value}`
    if (!value || seen.has(id)) continue
    seen.add(id)
    out.push({ name, label, shared, value })
  }
  return out
}

export function detailLabel(t: Translate, field: DetailField): string {
  return field.shared ? t(`field.${field.label}`) : t(`notifications.field.${field.label}`)
}

function withOpen(path: string, id: string): string {
  return `${path}?${OPEN_PARAM}=${encodeURIComponent(id)}`
}

const OPEN_PARAM = 'open'

export function entityRoute(
  linkEntity: string | null,
  linkId: string | null,
  params?: Record<string, string>,
): string | null {
  if (!linkEntity || !linkId) return null
  switch (linkEntity) {
    case 'time_day':
      return timeDayHref(params?.date ?? params?.day, params?.kind)
    case 'employee':
    case 'supervisor':
      return `/hr/${linkId}`
    case 'sales_document':
      return `/finance/sales/${linkId}`
    case 'purchase_request':
      return withOpen('/finance/requests', linkId)
    case 'leave_application':
      return withOpen('/leave', linkId)
    case 'salary_period':
      return '/payroll'
    default:
      return null
  }
}

/** Pages for notices whose link is absent or names no record of the reader's own. */
const KIND_FALLBACK: Readonly<Record<string, string>> = {
  timesheet_needs_approval: '/time?scope=awaiting',
  timesheet_approval_deadline: '/time?scope=awaiting',
  salary_cutoff_pending: '/time',
  salary_cutoff_last_day: '/time',
  timesheet_pending_at_close: '/time?scope=backlog',
  timesheet_weekly_reminder: '/time',
  overwork_14_days: '/time',
  leave_approval_pending: leaveHref('awaiting'),
  annual_leave_unused: leaveHref('own', ''),
  leave_override_granted: leaveHref('own', ''),
  leave_settlement_decided: leaveHref('own', ''),
  roster_departure_soon: '/roster/my',
  roster_plan_changed: '/roster/my',
  oreas_request_received: '/inventory?view=oreas&section=requests',
  oreas_request_resolved: '/inventory?view=oreas&section=requests',
  approval_backlog_digest: '/time?scope=admin',
  equipment_request_decided: '/me',
}

/** The peer's leave id, not the reader's: opening it would show someone else's record or nothing. */
const PEER_KINDS: ReadonlySet<string> = new Set(['leave_peer_blocked', 'leave_peer_clash'])

/** Workers key reminders by the person they are about; only the HR kinds (no fallback) open that record. */
const PERSON_LINKS: ReadonlySet<string> = new Set(['employee', 'supervisor'])

export type DigestKind = 'time' | 'leave'

export interface DigestLink {
  kind: DigestKind
  count: number
  href: string
}

const DIGEST_HREF: Readonly<Record<DigestKind, string>> = {
  time: '/time?scope=admin',
  leave: leaveHref('awaiting'),
}

function countParam(raw: string | undefined): number {
  const value = Number(raw)
  return Number.isInteger(value) && value > 0 ? value : 0
}

/** The backlog digest's per-kind links, each only when its own count is above zero; a notice from before the split has none. */
export function digestLinks(n: NoticeLike): DigestLink[] {
  if (n.kind !== 'approval_backlog_digest') return []
  return (['time', 'leave'] as const).flatMap((kind) => {
    const count = countParam(n.params?.[`${kind}_count`])
    return count > 0 ? [{ kind, count, href: DIGEST_HREF[kind] }] : []
  })
}

/** Which digest sentence names the counts the notice carries; `null` keeps the old total-only sentence. */
export function digestTextKey(n: NoticeLike): string | null {
  const kinds = digestLinks(n).map((link) => link.kind)
  if (kinds.length === 2) return 'notifications.kind.approval_backlog_digest_both'
  if (kinds.length === 1) return `notifications.kind.approval_backlog_digest_${kinds[0]}`
  return null
}

export function noticeHref(n: NoticeLike): string | null {
  if (PEER_KINDS.has(n.kind)) return leaveHref('own', '')
  const digest = digestLinks(n)
  if (digest.length > 0) return digest[0].href
  const fallback = KIND_FALLBACK[n.kind]
  if (fallback && n.link_entity && PERSON_LINKS.has(n.link_entity)) return fallback
  return entityRoute(n.link_entity, n.link_id, n.params) ?? fallback ?? null
}

const EXPIRED_PEOPLE_SHOWN = 5

export interface ExpiredDays {
  lines: string[]
  more: number
}

function isExpiredPerson(value: unknown): value is { name: string; days: [string, string][] } {
  if (!value || typeof value !== 'object') return false
  const p = value as Record<string, unknown>
  return (
    typeof p.name === 'string' &&
    Array.isArray(p.days) &&
    p.days.every((d) => Array.isArray(d) && typeof d[0] === 'string' && typeof d[1] === 'string')
  )
}

/** "Name: date (kind), …" per person from `rejected_days`, the first five and a count of the rest; empty when absent or unreadable. */
export function expiredDays(n: NoticeLike, kindLabel: (kind: string) => string): ExpiredDays {
  const raw = n.params?.rejected_days
  if (typeof raw !== 'string' || !raw) return { lines: [], more: 0 }
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return { lines: [], more: 0 }
  }
  if (!Array.isArray(parsed)) return { lines: [], more: 0 }
  const people = parsed.filter(isExpiredPerson)
  const lines = people
    .slice(0, EXPIRED_PEOPLE_SHOWN)
    .map((p) => `${p.name}: ${p.days.map(([day, kind]) => `${day} (${kindLabel(kind)})`).join(', ')}`)
  return { lines, more: Math.max(0, people.length - EXPIRED_PEOPLE_SHOWN) }
}
